import { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { API } from 'src/common/config/API';
import { 
  localStorageMethod, 
  priceTagRequestPayload, 
  sendInterComEvent, 
  setGetUserName 
} from 'src/common/config/FunctionHelper';
import { 
  beginCheckoutTrackEvent, 
  selectTrialPlanTrackEvent 
} from 'src/common/Analytics';
import { posthogService, PostHogEvent } from 'src/common/services/posthogService';
import { 
  setIsDowngradedWithSites, 
  setStateStripeSubscription
} from 'src/redux/userReducer';
import { setIsDowngradeToFree } from 'src/redux/dashboard';
import { setIsUpgradedToSeo } from 'src/redux/localSeo';
// Note: Some imports might need adjustment based on where they actually are in Redux
import { removeUtmCookie } from 'src/common/config/FunctionHelperPartTwo';
import { stripeStatusTrial, localseoSignup, localseoSignupSqspFlow, planNames } from 'src/common/config/TextMessage';

export const usePlanSubscription = (
  activePlanName: string,
  subscriptionDetails: any,
  selectedPlanName: any,
  planDetails: any,
  userDetails: any,
  currentPlanId: string,
  setCurrentPlanId: (id: string) => void,
  subscriptionId: string,
  setSubscriptionId: (id: string) => void
) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [changePlanSuccess, setChangePlanSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState<any>(null);
  const [changeSubscription, setChangeSubscription] = useState(false);

  const handleUpgradeDowngradePlan = useCallback(async (
    planId: string,
    planChangeStatus: string,
    downgradeFeedback?: string,
    siteIds?: string[],
    selectedOption?: any,
    couponId?: string,
    businessIds?: string[]
  ) => {
    try {
      setLoading(true);
      const trackPlanName = selectedPlanName?.metadata?.display_name?.toLocaleLowerCase();

      if (
        activePlanName === planDetails.free &&
        subscriptionDetails?.subscription_id &&
        !subscriptionDetails?.hasPaymentMethod
      ) {
        let payload = priceTagRequestPayload(selectedPlanName);
        beginCheckoutTrackEvent(trackPlanName, payload);
        API.checkOut(API.EndPoint.checkOut, API.Method.post, undefined, {
          planId: planId,
          email: setGetUserName(),
          couponId: couponId || null
        }, (response) => {
          const responseDetails = { ...response?.data };
          if (responseDetails.status === 'success') {
            localStorageMethod('set', 'free_to_pro', 'true');
            // ... Intercom and analytics logic
            setLoading(false);
            if (responseDetails.redirectUrl) {
              window.location.href = responseDetails.redirectUrl;
            }
          }
          setLoading(false);
        });
      }

      if (subscriptionId && (activePlanName !== planDetails.free || (activePlanName === planDetails.free && subscriptionDetails?.hasPaymentMethod))) {
        const isV1Plan = subscriptionDetails?.planVersion === 'v1';
        API.upgradeDowngradePlan(API.EndPoint.upgradeDowngradePlan, API.Method.post, undefined, {
          email: userDetails?.email,
          subscription_id: subscriptionId,
          planId,
          type: planChangeStatus,
          downgradeFeedback,
          siteIds: siteIds,
          selectedDowngradeReason: selectedOption,
          couponId: couponId || null,
          businessIds: businessIds,
          is_migration: isV1Plan ? true : false
        }, (response) => {
          if (response.data.status === 'success') {
            const isUpgrade = planChangeStatus?.toLowerCase().includes('upgrade');
            posthogService.track(
              isUpgrade ? PostHogEvent.BILLING_PLAN_UPGRADED : PostHogEvent.BILLING_PLAN_DOWNGRADED,
              {
                plan_id: planId,
                plan_name: trackPlanName,
                previous_plan: activePlanName,
                type: planChangeStatus
              }
            );
            // ... success logic
            setLoading(false);
            setChangePlanSuccess(true);
            setChangeSubscription(prev => !prev);
          } else {
            setLoading(false);
            setErrorMessage({ status: response?.data?.status, message: 'Something went wrong' });
          }
        });
      }
    } catch (err) {
      setLoading(false);
    }
  }, [selectedPlanName, activePlanName, planDetails, subscriptionDetails, subscriptionId, userDetails]);

  useEffect(() => {
    if (changeSubscription) {
      API.subscriptionDetailsStripe(API.EndPoint.subscriptionDetailsStripe, API.Method.post, undefined, { 
        email: setGetUserName() 
      }, (response) => {
        const responseDetails = { ...response?.data };
        if (responseDetails?.subscription?.plan_name?.id) {
          setCurrentPlanId(responseDetails?.subscription?.plan_name?.id);
        }
        // dispatch(setStateStripeSubscription(responseDetails?.subscription));
        setSubscriptionId(responseDetails?.subscription?.subscription_id);
      });
    }
  }, [changeSubscription]);

  return { loading, changePlanSuccess, setChangePlanSuccess, errorMessage, setErrorMessage, handleUpgradeDowngradePlan };
};
